import React from "react";
import { Icon } from "../UIElements/Icon";
import { useGlobalUtils } from "../../../lib/globalUtils";

const LoyaltyCard = ({ loyaltyLevel }) => {
  const { parseDate } = useGlobalUtils();

  const getLoyaltyIcon = (level) => {
    switch (level) {
      case 1:
        return "Star";
      case 2:
        return "Award";
      case 3:
        return "Crown";
      default:
        return "Star";
    }
  };

  const getLoyaltyColor = (level) => {
    switch (level) {
      case 1:
        return "text-blue-400";
      case 2:
        return "text-purple-400";
      case 3:
        return "text-yellow-400";
      default:
        return "text-blue-400";
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800 p-6">
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="rounded-lg bg-gray-700 p-3">
            <Icon
              name={getLoyaltyIcon(loyaltyLevel.id)}
              className={`h-6 w-6 ${getLoyaltyColor(loyaltyLevel.id)}`}
            />
          </div>
          <div>
            <h4 className="text-lg font-semibold text-white">
              {loyaltyLevel.name}
            </h4>
            <p className="text-sm text-gray-400">Loyalty Level</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Conditional Layout: Two columns with image, single column without */}
        {loyaltyLevel.image ? (
          <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
            {/* Image Section */}
            <div className="text-center md:text-left">
              <div className="relative inline-block">
                <div
                  className={`absolute -inset-1 rounded-2xl ${getLoyaltyColor(loyaltyLevel.id).replace("text-", "bg-").replace("-400", "-500")} opacity-75 blur-sm`}
                ></div>
                <div className="relative rounded-2xl border border-gray-600 bg-white p-4">
                  <img
                    src={loyaltyLevel.image}
                    alt={loyaltyLevel.name}
                    className="mx-auto h-45 w-45 rounded-xl object-cover shadow-lg md:mx-0"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                  <div className="mx-auto flex hidden h-45 w-45 items-center justify-center rounded-xl md:mx-0">
                    <Icon
                      name={getLoyaltyIcon(loyaltyLevel.id)}
                      className={`h-16 w-16 ${getLoyaltyColor(loyaltyLevel.id)}`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="space-y-4">
              {/* Points Requirements */}
              <div className="text-center md:text-left">
                <p className="mb-1 text-sm text-gray-400">
                  Minimum Points Required
                </p>
                <p
                  className={`text-3xl font-bold ${getLoyaltyColor(loyaltyLevel.id)}`}
                >
                  {loyaltyLevel.minimum_points.toLocaleString()} pts
                </p>
              </div>

              {/* Loyalty Details */}
              <div className="space-y-3">
                {loyaltyLevel.description && (
                  <div className="mb-3 rounded-lg bg-gray-700/50 p-3">
                    <p className="text-sm text-gray-300">
                      <span className="font-medium text-white">
                        Description:
                      </span>{" "}
                      {loyaltyLevel.description}
                    </p>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">
                    Accumulation Rate:
                  </span>
                  <span className="text-sm font-medium text-white">
                    {loyaltyLevel.accumulation_rate}
                  </span>
                </div>

                {loyaltyLevel.benefits?.length > 0 && (
                  <div className="flex items-start">
                    <span className="min-w-[80px] text-sm text-gray-400">
                      Benefits:
                    </span>
                    <ul className="ml-4 max-w-xs list-inside list-disc space-y-1 text-right text-sm text-white">
                      {loyaltyLevel.benefits.map((benefit, idx) => (
                        <li key={idx}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {loyaltyLevel.maximum_accumulation_percentage && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">
                      Max Accumulation:
                    </span>
                    <span className="text-sm text-white">
                      {loyaltyLevel.maximum_accumulation_percentage}%
                    </span>
                  </div>
                )}

                {loyaltyLevel.maximum_accumulation_fixed && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">
                      Max Fixed Points:
                    </span>
                    <span className="text-sm text-white">
                      {loyaltyLevel.maximum_accumulation_fixed.toLocaleString()}{" "}
                      pts
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Created:</span>
                  <span className="text-sm text-white">
                    {parseDate(loyaltyLevel.created_at)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">Last Updated:</span>
                  <span className="text-sm text-white">
                    {parseDate(loyaltyLevel.updated_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Points Requirements */}
            <div className="text-center">
              <p className="mb-1 text-sm text-gray-400">
                Minimum Points Required
              </p>
              <p
                className={`text-3xl font-bold ${getLoyaltyColor(loyaltyLevel.id)}`}
              >
                {loyaltyLevel.minimum_points.toLocaleString()} pts
              </p>
            </div>

            {/* Loyalty Details */}
            <div className="space-y-3">
              {loyaltyLevel.description && (
                <div className="mb-3 rounded-lg bg-gray-700/50 p-3">
                  <p className="text-sm text-gray-300">
                    <span className="font-medium text-white">Description:</span>{" "}
                    {loyaltyLevel.description}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">
                  Accumulation Rate:
                </span>
                <span className="text-sm font-medium text-white">
                  {loyaltyLevel.accumulation_rate}
                </span>
              </div>

              {loyaltyLevel.benefits?.length > 0 && (
                <div className="flex items-start">
                  <span className="min-w-[80px] text-sm text-gray-400">
                    Benefits:
                  </span>
                  <ul className="ml-4 max-w-xs list-inside list-disc space-y-1 text-right text-sm text-white">
                    {loyaltyLevel.benefits.map((benefit, idx) => (
                      <li key={idx}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              )}

              {loyaltyLevel.maximum_accumulation_percentage && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">
                    Max Accumulation:
                  </span>
                  <span className="text-sm text-white">
                    {loyaltyLevel.maximum_accumulation_percentage}%
                  </span>
                </div>
              )}

              {loyaltyLevel.maximum_accumulation_fixed && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-400">
                    Max Fixed Points:
                  </span>
                  <span className="text-sm text-white">
                    {loyaltyLevel.maximum_accumulation_fixed.toLocaleString()}{" "}
                    pts
                  </span>
                </div>
              )}

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Created:</span>
                <span className="text-sm text-white">
                  {parseDate(loyaltyLevel.created_at)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-400">Last Updated:</span>
                <span className="text-sm text-white">
                  {parseDate(loyaltyLevel.updated_at)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoyaltyCard;
